export const adjustHexColor = (percent: number, hex: string): string => {
  // Ensure the hex is valid
  const isValidHex = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(hex);
  if (!isValidHex) {
    throw new Error("Invalid HEX color.");
  }

  // Normalize to 6-character hex
  let normalizedHex = hex.slice(1);
  if (normalizedHex.length === 3) {
    normalizedHex = normalizedHex.split("").map(c => c + c).join("");
  }

  // Parse R, G, B values
  const r = parseInt(normalizedHex.slice(0, 2), 16);
  const g = parseInt(normalizedHex.slice(2, 4), 16);
  const b = parseInt(normalizedHex.slice(4, 6), 16);

  // Adjust color values toward white or black
  const adjust = (color: number) => {
    if (percent > 0) {
      return Math.round(color + ((255 - color) * percent) / 100); // Lighten toward white
    } else {
      return Math.round(color + (color * percent) / 100); // Darken toward black
    }
  };

  const newR = Math.min(255, Math.max(0, adjust(r)));
  const newG = Math.min(255, Math.max(0, adjust(g)));
  const newB = Math.min(255, Math.max(0, adjust(b)));

  // Convert back to HEX and return
  return `#${((1 << 24) + (newR << 16) + (newG << 8) + newB)
    .toString(16)
    .slice(1)}`;
}

export const formatCurrency = (number: any) => {
  const parts = parseFloat(number?.toString() || 0).toFixed(2).toString().split(".");
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return `$${integerPart}.${parts[1]}`;
}

export const filterNestedKeys = (obj: any, keys: string[]): any => {
  const result: any = {};

  keys.forEach((key) => {
    const keyParts = key.split('.');
    let current = result;
    let src = obj;

    keyParts.forEach((part, index) => {
      if (src && src[part] !== undefined) {
        if (index === keyParts.length - 1) {
          current[part] = src[part];
        } else {
          current[part] = current[part] || {};
          current = current[part];
          src = src[part];
        }
      }
    });
  });

  return result;
};


// Build the filtered object
export const filterByKeys = (keys: any[], values: any) => {
  const getValueByPath = (obj: any, path: string) => path.split(".").reduce((o, key) => (o ? o[key] : undefined), obj);
  return keys.reduce((acc: any, key: string) => {
    const value = getValueByPath(values, key);
    if (value !== undefined) {
      const path = key.split(".");
      path.reduce((nested, part, idx) => {
        if (idx === path.length - 1) {
          nested[part] = value; // Set the value
        } else {
          nested[part] = nested[part] || {}; // Ensure intermediate objects exist
        }
        return nested[part];
      }, acc);
    }
    return acc;
  }, {});
}


export const getNestedValue = (obj: any, key: any) => {
  return key.split('.').reduce((acc: any, part: any) => acc && acc[part], obj);
};

export const findNestedValue = (arr: any[], key: string, value: string) => {
  return arr.find(obj => {
    const keys = key.split('.');
    let current = obj;

    for (const k of keys) {
      if (current && k in current) {
        current = current[k];
      } else {
        return false;
      }
    }

    return current === value;
  });
}

export const setNestedValue = (obj: any, path: any, value: any) => {
  const keys = path.split('.');
  let current = obj;
  keys.forEach((key: any, index: any) => {
      if (index === keys.length - 1) {
          current[key] = value;
      } else {
          current[key] = current[key] || {};
          current = current[key];
      }
  });
  return obj;
};

export const findInNestedArray = (filters: any, key: string, value: string | number): any => {
  return Array.isArray(filters)
    ? filters.flatMap(item =>
        Array.isArray(item) ? findInNestedArray(item, key, value) : item
      ).find(item => item?.[key] === value)
    : null;
};